import React, { useMemo } from 'react'
import { alpha, createTheme, useTheme, Theme, ThemeProvider, SxProps } from '@mui/material/styles'
import { FormHelperTextProps } from '@mui/material/FormHelperText'
import { InputBaseProps } from '@mui/material/InputBase'
import { InputLabelProps } from '@mui/material/InputLabel'
import { InputProps as StandardInputProps } from '@mui/material/Input'
import TextField, { TextFieldProps } from '@mui/material/TextField'

import colors from '@/assets/scss/_themes-vars.module.scss'

export type TextInputProps = {
  /**
   * id of the input useful for e2e testing ;)
   */
  id?: string

  /**
   * This prop helps users to fill forms faster, especially on mobile devices.
   */
  autoComplete?: string
  /**
   * If `true`, the `input` element is focused during the first mount.
   * @default false
   */
  autoFocus?: boolean
  /**
   * The default value. Use when the component is not controlled.
   */
  defaultValue?: unknown
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: boolean
  /**
   * If `true`, the component is displayed in an error state.
   * @default false
   */
  error?: boolean
  /**
   * Props applied to the FormHelperText element.
   */
  FormHelperTextProps?: Partial<FormHelperTextProps>
  /**
   * If `true`, the input will take up the full width of its container.
   * @default false
   */
  fullWidth?: boolean
  /**
   * The helper text content.
   */
  helperText?: React.ReactNode
  /**
   * Props applied to the Input element.
   * It will be a [`OutlinedInput`](https://mui.com/api/outlined-input/)
   */
  InputProps?: Partial<StandardInputProps>
  /**
   * Props applied to the [`InputLabel`](https://mui.com/api/input-label/) element.
   */
  InputLabelProps?: Partial<InputLabelProps>
  /**
   * [Attributes](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Attributes) applied to the `input` element.
   */
  inputProps?: InputBaseProps['inputProps']
  /**
   * Pass a ref to the `input` element.
   */
  inputRef?: React.Ref<any>
  /**
   * The label content.
   */
  label?: React.ReactNode
  /**
   * If `true`, a `textarea` element is rendered instead of an input.
   * @default false
   */
  multiline?: boolean
  /**
   * Name attribute of the `input` element.
   */
  name?: string
  onBlur?: InputBaseProps['onBlur']
  /**
   * Callback fired when the value is changed.
   *
   * @param {object} event The event source of the callback.
   * You can pull out the new value by accessing `event.target.value` (string).
   */
  onChange?: StandardInputProps['onChange']
  onFocus?: StandardInputProps['onFocus']
  /**
   * The short hint displayed in the `input` before the user enters a value.
   */
  placeholder?: string
  /**
   * If `true`, the label is displayed as required and the `input` element is required.
   * @default false
   */
  required?: boolean
  /**
   * Number of rows to display when multiline option is set to true.
   */
  rows?: string | number
  /**
   * Maximum number of rows to display when multiline option is set to true.
   */
  maxRows?: string | number
  /**
   * Minimum number of rows to display when multiline option is set to true.
   */
  minRows?: string | number
  /**
   * It prevents the user from changing the value of the field
   * (not from interacting with the field).
   */
  readOnly?: boolean
  size?: TextFieldProps['size']
  /**
   * Type of the `input` element. It should be [a valid HTML5 input type](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Form_%3Cinput%3E_types).
   */
  type?: React.InputHTMLAttributes<unknown>['type']
  /**
   * The value of the `input` element, required for a controlled component.
   */
  value?: unknown
  sx?: SxProps<Theme>
}

const createTextFieldTheme = (defaultTheme: Theme) => {
  return createTheme(defaultTheme, {
    components: {
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: colors.gray700,
            lineHeight: '1.25rem',
            '& + .MuiOutlinedInput-root': {
              marginTop: 4,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          outlined: {
            fontSize: '0.875rem',
            fontWeight: 500,
            transform: 'none',
            '& + .MuiOutlinedInput-root': {
              marginTop: 24,
            },
            '&.Mui-focused:not(.Mui-error)': {
              color: colors.gray700,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            lineHeight: '1.25rem',
            boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05), 0px 0px 0px 2px #FFFFFF',
            padding: 0,
            '&&& .MuiInputBase-input': {
              // tripple '&' to override default behaviour
              '&::-webkit-input-placeholder': { opacity: '1 !important' },
              '&::-moz-placeholder': { opacity: '1 !important' }, // Firefox 19+
              '&:-ms-input-placeholder': { opacity: '1 !important' }, // IE11
              '&::-ms-input-placeholder': { opacity: '1 !important' }, // Edge
            },
            '&:hover:not(.Mui-disabled):not(.Mui-error)': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.gray300,
              },
            },
            '&.Mui-focused': {
              // boxShadow: `0 0 0 3px ${alpha(colors.primaryMain, 0.12)}`,
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: 1,
                borderColor: colors.primaryMain?.startsWith('#') ? alpha(colors.primaryMain, 0.38) + '!important' : colors.primaryMain,
              },
            },
          },
          input: {
            fontSize: '14px',
            padding: '8px 12px',
            height: 'auto',
            border: '1px solid rgba(0,0,0,0)',
          },
          notchedOutline: {
            top: 0,
            borderColor: colors.gray300,
            '& legend': {
              display: 'none',
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            '::placeholder': {
              color: colors.gray500,
            },
            '&.Mui-disabled': {
              cursor: 'not-allowed',
            },
          },
          inputAdornedEnd: {
            '&&': {
              paddingRight: 2,
            },
          },
          inputAdornedStart: {
            '&&': {
              paddingLeft: 2,
            },
          },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            color: colors.gray500,
          },
        },
      },
    },
  })
}

export const TextInput = React.forwardRef(function TextInput(props: TextInputProps, ref: React.Ref<HTMLDivElement>): JSX.Element {
  const { InputProps, InputLabelProps, readOnly, ...rest } = props
  const defaultTheme = useTheme()
  const textFieldTheme = useMemo(() => createTextFieldTheme(defaultTheme), [defaultTheme])

  return (
    <ThemeProvider theme={textFieldTheme}>
      <TextField
        {...rest}
        ref={ref}
        InputProps={{
          ...InputProps,
          readOnly,
        }}
        InputLabelProps={{
          ...InputLabelProps,
          shrink: true,
        }}
        variant="outlined"
      />
    </ThemeProvider>
  )
})

export default TextInput
